import React, { useState, useEffect, useRef } from 'react';
import './HeroVideoAdmin.css';

const HeroVideoAdmin = ({ videoUrl, onChange }) => {
  const [previewUrl, setPreviewUrl] = useState(videoUrl || '');
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // Sync preview URL with prop changes
  useEffect(() => {
    setPreviewUrl(videoUrl || '');
  }, [videoUrl]);

  const processVideoFile = async (file) => {
    if (!file.type.startsWith('video/')) {
      alert('Veuillez sélectionner un fichier vidéo');
      return;
    }

    // Check file size (max 50MB)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      alert(`⚠️ Fichier trop volumineux!\n\nTaille actuelle: ${(file.size / 1024 / 1024).toFixed(2)}MB\nTaille maximale: 50MB\n\nVeuillez compresser votre vidéo.`);
      return;
    }

    setIsUploading(true);

    try {
      console.log(`📤 Uploading video: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)`);
      
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('files', file);
      formData.append('type', 'video');
      
      // Upload to backend - get token from correct storage key
      const token = sessionStorage.getItem('auth_token') || sessionStorage.getItem('token');
      
      if (!token) {
        throw new Error('Non authentifié. Veuillez vous reconnecter.');
      }
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://dentoxcellence-backend.dentoxcellence.workers.dev/api'}/media/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Upload failed: ${response.statusText} - ${errorText}`);
      }
      
      const result = await response.json();
      console.log('✅ Video uploaded successfully:', result);
      
      // Get the video URL from response (backend returns file_url)
      const videoUrl = result.files && result.files[0] ? result.files[0].file_url : null;
      
      if (!videoUrl) {
        throw new Error('No video URL returned from server');
      }
      
      console.log('📹 Video URL:', videoUrl);
      
      // Store the full backend URL
      const apiBaseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'https://dentoxcellence-backend.dentoxcellence.workers.dev';
      const fullVideoUrl = `${apiBaseUrl}${videoUrl}`;
      console.log('📹 Full Video URL:', fullVideoUrl);
      
      setPreviewUrl(fullVideoUrl);
      onChange(fullVideoUrl);
      setIsUploading(false);
      
    } catch (error) {
      console.error('❌ Error uploading video:', error);
      alert(`Erreur lors du téléchargement de la vidéo:\n${error.message}\n\nVeuillez vérifier que le backend est accessible.`);
      setIsUploading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      processVideoFile(files[0]);
    }
  };

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      processVideoFile(files[0]);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveVideo = () => {
    console.log('🗑️ Video removed');
    setPreviewUrl('');
    onChange('');
  };

  return (
    <div className="hero-video-admin">
      <h3 className="section-title">
        <i className="fas fa-video"></i>
        Vidéo d'arrière-plan de la bannière principale
      </h3>
      
      <div className="video-upload-section">
        {/* Drag & Drop Upload Area */}
        <div 
          className={`drag-drop-zone ${isDragOver ? 'drag-over' : ''} ${isUploading ? 'uploading' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleBrowseClick}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          
          {isUploading ? (
            <div className="upload-status">
              <div className="spinner"></div>
              <p>Traitement de la vidéo en cours...</p>
            </div>
          ) : (
            <>
              <i className="fas fa-cloud-upload-alt upload-icon"></i>
              <h4>Glissez-déposez votre vidéo ici</h4>
              <p>ou cliquez pour parcourir</p>
              <div className="file-requirements">
                <span>MP4, WebM, MOV</span>
                <span>•</span>
                <span style={{color: '#10b981', fontWeight: 'bold'}}>Max 50MB</span>
                <span>•</span>
                <span style={{color: '#3b82f6', fontWeight: 'bold'}}>~10 secondes recommandé</span>
              </div>
            </>
          )}
        </div>

        {/* Video Preview */}
        {previewUrl && (
          <div className="video-preview-container">
            <div className="video-preview">
              <video 
                src={previewUrl} 
                controls 
                muted 
                loop
                style={{
                  width: '100%',
                  maxHeight: '400px',
                  borderRadius: '12px',
                  objectFit: 'cover'
                }}
                onError={(e) => {
                  console.error('❌ Erreur de chargement vidéo:', previewUrl);
                }}
              >
                Votre navigateur ne supporte pas la balise vidéo.
              </video>
            </div>
            
            <button 
              onClick={handleRemoveVideo}
              className="btn-remove-video"
              title="Supprimer la vidéo"
            >
              <i className="fas fa-trash-alt"></i>
              Supprimer la vidéo
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default HeroVideoAdmin;

